<template>
  <OrderCard :title="t('order.cardTitles.deliveryAddress')">
    <template #top-right>
      <Dialog>
        <DialogTrigger as-child>
          <Button variant="ghost" size="icon" class="hover:text-primary">
            <Info class="size-5" />
          </Button>
        </DialogTrigger>
        <DialogContent class="max-w-md">
          <DialogHeader>
            <DialogTitle>{{ t("order.info.deliveryInfo") }}</DialogTitle>
          </DialogHeader>
          <div class="text-sm text-muted-foreground space-y-2">
            <p>{{ t("order.info.deliveryArea") }}</p>
            <p>
              {{ t("order.info.deliveryAreaDetails") }}
              <a href="/dostawy-informacje">{{ t("order.info.here") }}</a>
            </p>
          </div>
        </DialogContent>
      </Dialog>
    </template>
    <FormField v-slot="{ componentField }" name="person.address.location_type">
      <FormItem>
        <FormLabel>{{ t("order.formLabels.localization") }}</FormLabel>
        <Select v-bind="componentField">
          <FormControl>
            <SelectTrigger class="!h-10 w-full">
              <SelectValue
                :placeholder="t('order.formLabels.localizationPlaceholder')"
              />
            </SelectTrigger>
          </FormControl>
          <SelectContent>
            <SelectGroup>
              <SelectItem
                v-for="type in Object.values(LocationType)"
                :key="type"
                :value="type"
              >
                {{ LocationTypeLabels[type] }}
              </SelectItem>
            </SelectGroup>
          </SelectContent>
        </Select>
        <FormMessage />
      </FormItem>
    </FormField>

    <div></div>
    <FormField v-slot="{ componentField }" name="person.address.street_name">
      <FormItem>
        <FormLabel for="street_name">{{
          t("order.formLabels.street")
        }}</FormLabel>
        <slot name="street" :component-field="componentField">
          <FormControl>
            <Input
              id="street_name"
              v-bind="componentField"
              autocomplete="address-line1"
            />
          </FormControl>
        </slot>
        <FormMessage />
      </FormItem>
    </FormField>
    <div></div>

    <FormField
      v-slot="{ componentField }"
      name="person.address.building_number"
    >
      <FormItem>
        <FormLabel for="building_number">{{
          t("order.formLabels.buildingNumber")
        }}</FormLabel>
        <FormControl>
          <Input
            id="building_number"
            v-bind="componentField"
            autocomplete="address-line2"
          />
        </FormControl>
        <FormMessage />
      </FormItem>
    </FormField>

    <FormField
      v-slot="{ componentField }"
      name="person.address.apartment_number"
    >
      <FormItem>
        <FormLabel for="apartment_number">{{
          t("order.formLabels.apartmentNumber")
        }}</FormLabel>
        <FormControl>
          <Input
            id="apartment_number"
            v-bind="componentField"
            autocomplete="address-line3"
          />
        </FormControl>
        <FormMessage />
      </FormItem>
    </FormField>

    <FormField v-slot="{ componentField }" name="person.address.postal_code">
      <FormItem>
        <FormLabel for="postal_code">{{
          t("order.formLabels.postalCode")
        }}</FormLabel>
        <FormControl>
          <Input
            id="postal_code"
            v-bind="componentField"
            autocomplete="postal-code"
            maxlength="6"
            @input="(e: Event) => {
              const input = e.target as HTMLInputElement;
              let val = input.value.replace(/\D/g, '');
              if (val.length > 2) val = val.slice(0, 2) + '-' + val.slice(2, 5);
              input.value = val;
              componentField.onChange(val);
            }"
          />
        </FormControl>
        <FormMessage />
      </FormItem>
    </FormField>

    <FormField v-slot="{ componentField }" name="person.address.city">
      <FormItem>
        <FormLabel for="city">{{ t("order.formLabels.city") }}</FormLabel>
        <FormControl>
          <Input
            id="city"
            v-bind="componentField"
            autocomplete="address-level2"
          />
        </FormControl>
        <FormMessage />
      </FormItem>
    </FormField>

    <FormField
      v-if="showDistrict"
      v-slot="{ componentField }"
      name="person.address.district"
    >
      <FormItem>
        <FormLabel for="district">{{
          t("order.formLabels.district")
        }}</FormLabel>
        <FormControl>
          <Input
            id="district"
            v-bind="componentField"
            autocomplete="address-level3"
          />
        </FormControl>
        <FormMessage />
      </FormItem>
    </FormField>

    <FormField
      v-if="showDriverNote"
      v-slot="{ componentField }"
      name="person.address.driver_note"
    >
      <FormItem class="sm:col-span-2">
        <FormLabel for="driver_note">
          {{ t("order.formLabels.driverAddressNote") }}
        </FormLabel>
        <FormControl>
          <Textarea
            id="driver_note"
            v-bind="componentField"
            :placeholder="t('order.formLabels.driverAddressNotePlaceholder')"
            maxlength="2000"
            rows="4"
          />
        </FormControl>
        <FormMessage />
      </FormItem>
    </FormField>
  </OrderCard>
</template>

<script setup lang="ts">
import { OrderCard } from "../../card";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../../dialog";
import { Button } from "../../button";
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "../../form";
import { Input } from "../../input";
import { Textarea } from "../../textarea";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
  SelectGroup,
} from "../../select";
import { LocationTypeLabels, LocationType } from "@acme/api";
import { useI18n } from "vue-i18n";
import { Info } from "lucide-vue-next";

withDefaults(
  defineProps<{
    showDistrict?: boolean;
    showDriverNote?: boolean;
  }>(),
  {
    showDistrict: false,
    showDriverNote: false,
  },
);

const { t } = useI18n();
</script>
